import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const PartnerContext = createContext({ partner: null, isPartner: false, loading: false });

export const usePartner = () => useContext(PartnerContext);

/**
 * Partner detection, two strategies:
 *  1. URL path `/p/<slug>/...`         → explicit partner prefix
 *  2. Subdomain `<slug>.<rest>`         → white-label production domains
 *     (e.g. `bookingexperts.kiosk-configurator.com`)
 *
 * Ignored subdomains (not treated as partner): `www`, `app`, `preview`,
 * `localhost`, numeric IPs, and any host that looks like a preview /
 * staging platform (*.preview.emergentagent.com, *.vercel.app, *.netlify.app).
 *
 * If path-slug is present it wins over subdomain-slug.
 */
const IGNORED_SUBDOMAINS = new Set([
  "www", "app", "admin", "api", "staging", "preview", "dev", "test", "localhost",
]);

/**
 * Custom vanity domains that should resolve to a specific partner slug.
 *
 * Two sources, merged at runtime:
 *  1. STATIC_DOMAIN_MAP below, fallback / boot-cache. Kept tiny on purpose;
 *     production should rely on the backend host-map (see below).
 *  2. Dynamic host-map fetched from `/api/partners/host-map` on first render,
 *     cached in localStorage. Lets admins add new partner domains via the
 *     partner.domains DB field WITHOUT a frontend deploy.
 *
 * Example DB shape:  partner.domains = ["bookingszuil.bookingexperts.com", ...]
 */
const STATIC_DOMAIN_MAP = {
  "bookingszuil.bookingexperts.com": "bookingexperts",
  "kiosk.bookingexperts.com":         "bookingexperts",
  "kiosk.recranet.com":               "recranet",
  "kiosk.tommybookingsupport.nl":     "tommy",
  "kiosk.tommybookingsupport.com":    "tommy",
  "kiosk.recrasolutions.com":         "recra-solutions",
};

const HOST_MAP_CACHE_KEY = "kx:hostmap:v1";

// Read previously-fetched map from localStorage (synchronous, fast).
function readCachedHostMap() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage?.getItem(HOST_MAP_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch { return {}; }
}

function writeCachedHostMap(map) {
  if (typeof window === "undefined") return;
  try { window.localStorage?.setItem(HOST_MAP_CACHE_KEY, JSON.stringify(map || {})); } catch {}
}

function customDomainSlug(extraMap) {
  if (typeof window === "undefined") return null;
  const host = (window.location.hostname || "").toLowerCase();
  return (extraMap && extraMap[host]) || STATIC_DOMAIN_MAP[host] || null;
}

function subdomainFromHost() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  if (!host) return null;
  // Ignore IP addresses & localhost
  if (/^\d+(\.\d+){3}$/.test(host) || host === "localhost") return null;
  // Ignore preview / platform hosts (the subdomain there is a random ID, not a partner)
  if (/\.preview\.emergentagent\.com$/i.test(host)) return null;
  if (/\.vercel\.app$/i.test(host)) return null;
  if (/\.netlify\.app$/i.test(host)) return null;
  if (/\.onrender\.com$/i.test(host)) return null;
  const parts = host.split(".");
  if (parts.length < 3) return null; // e.g. kiosk-configurator.com has no partner subdomain
  const sub = parts[0].toLowerCase();
  if (IGNORED_SUBDOMAINS.has(sub)) return null;
  return sub;
}

export function PartnerProvider({ children }) {
  const location = useLocation();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(false);
  // Dynamic host→slug map fetched from backend (so admin can add domains
  // via partner.domains in DB without a frontend deploy). Boot value is
  // the locally-cached map so the first render is synchronous.
  const [dynamicHostMap, setDynamicHostMap] = useState(() => readCachedHostMap());

  // Fetch latest host-map from the API once on mount. Update both state and
  // localStorage cache so subsequent visits skip the round-trip.
  useEffect(() => {
    let mounted = true;
    api.get("/partners/host-map")
      .then((r) => {
        if (!mounted) return;
        const map = (r.data && typeof r.data === "object") ? r.data : {};
        setDynamicHostMap(map);
        writeCachedHostMap(map);
      })
      .catch(() => {/* keep cached map */});
    return () => { mounted = false; };
  }, []);

  // Resolve slug: path wins, then dynamic+static custom-domain map, then subdomain.
  const slug = useMemo(() => {
    const m = location.pathname.match(/^\/p\/([^/]+)(\/|$)/);
    if (m) return m[1];
    return customDomainSlug(dynamicHostMap) || subdomainFromHost();
  }, [location.pathname, dynamicHostMap]);

  useEffect(() => {
    let mounted = true;
    if (!slug) { setPartner(null); return; }
    setLoading(true);
    api.get(`/partners/${slug}`)
      .then((r) => { if (mounted) setPartner(r.data); })
      .catch(() => { if (mounted) setPartner(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  // Inject CSS vars for branding + optional partner theme mode ("light" → whole app flips to a white/clean skin)
  useEffect(() => {
    const root = document.documentElement;
    if (partner) {
      root.style.setProperty("--partner-primary", partner.primary_color || "#6FAA8A");
      root.style.setProperty("--partner-accent", partner.accent_color || "#D97742");
      if (partner.theme_mode === "light") {
        root.setAttribute("data-partner-theme", "light");
      } else {
        root.removeAttribute("data-partner-theme");
      }
      // Editorial-luxe theme extends globally (configurator, catalog, etc.)
      // when partner explicitly opts in via landing_use_luxe_theme.
      if (partner.landing_use_luxe_theme) {
        root.setAttribute("data-partner-luxe", "true");
        document.body.classList.add("luxe-active");
      } else {
        root.removeAttribute("data-partner-luxe");
        document.body.classList.remove("luxe-active");
      }
      // RECRA Solutions theme, dark olive + bright yellow + Anton/Inter
      if (partner.landing_use_recra_theme) {
        document.body.classList.add("recra-active");
      } else {
        document.body.classList.remove("recra-active");
      }
      // Recranet theme, dark navy + cyan + Work Sans
      if (partner.landing_use_recranet_theme) {
        document.body.classList.add("recranet-active");
      } else {
        document.body.classList.remove("recranet-active");
      }
      // Tommy theme, Outfit font + navy + orange accent
      if (partner.landing_use_tommy_theme) {
        document.body.classList.add("tommy-active");
      } else {
        document.body.classList.remove("tommy-active");
      }
      document.title = partner.name
        ? `${partner.name} · Kiosk Configurator`
        : "RECRA Solutions, Kiosk Configurator | Zelfbedieningszuilen op maat";
    } else {
      root.style.removeProperty("--partner-primary");
      root.style.removeProperty("--partner-accent");
      root.removeAttribute("data-partner-theme");
      root.removeAttribute("data-partner-luxe");
      document.body.classList.remove("luxe-active");
      document.body.classList.remove("recra-active");
      document.body.classList.remove("recranet-active");
      document.body.classList.remove("tommy-active");
      document.title = "RECRA Solutions, Kiosk Configurator | Zelfbedieningszuilen op maat";
    }
  }, [partner]);

  const value = useMemo(() => {
    // Path-prefix only applies when we matched via URL, not subdomain.
    const pathMatch = location.pathname.match(/^\/p\/([^/]+)(\/|$)/);
    const prefix = pathMatch ? `/p/${pathMatch[1]}` : "";
    return {
      partner,
      isPartner: !!partner,
      loading,
      slug,
      prefix,
      // Expose how partner was detected so UI can hint at it if needed.
      via: pathMatch ? "path" : (partner ? "subdomain" : null),
    };
  }, [partner, loading, slug, location.pathname]);

  return <PartnerContext.Provider value={value}>{children}</PartnerContext.Provider>;
}
